// V2 版式：divider（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/divider.ts 逐坐标移植
// 无 GEO（divider/closing 不在 v2geo dump 内），数值全部内联自后端文件并标注来源行。
// ink 满屏（wrapper 按 defaultBg 铺 skin.page）+ 巨号章节序号 + 一句话；不渲染 Chrome（meta 行自含）。

import { SPACING } from "../../lib/tokens.generated";
import { px, pt, titleSize, footOf } from "./shared";
import { luxeExt, LuxeDecorCircle } from "./luxe";
import {
  consultTheme, reportTheme, inkTheme,
  roman, cnNum, InkSeal, MemphisMotif,
} from "./themes";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2Divider({ c, skin, page, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // divider.ts:15 —— swiss 下 skin.accent 即 theme.accent（skin.ts:45），前端取解析后值即可
  const accent = mag ? skin.accentOn : skin.accent;
  // luxe：巨号章节序号降级为淡金水印（accentLight，divider.ts numColor 分支）
  const ext = luxeExt(skin);
  // consult（043 §4.2）：深底满屏上 navy 序号不可见——用 emerald 签名色；ink（§4.6）章节号用朱红
  const consultT = consultTheme(skin);
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const numColor = ext ? ext.accentLight
    : (consultT && skin.bg !== "paper") ? consultT.accentEmerald
      : inkT ? inkT.accent
        : accent;
  // 章节号文本：report 罗马数字（I/II/III…）；ink 中文数字（壹贰叁…）；其余两位阿拉伯
  const numText = reportT ? roman(page)
    : inkT ? cnNum(page)
      : String(page).padStart(2, "0");

  return (
    <>
      {/* 裸金装饰半圆（仅 luxe；先画，文字压其上） */}
      <LuxeDecorCircle skin={skin} variant="divider" />
      {/* memphis（043 §4.7）：章节页深底满屏上母题狂欢 */}
      <MemphisMotif skin={skin} variant="divider" />
      {/* 巨号章节序号（divider.ts:18-21）：静态装饰，取页序，不进可编辑值 */}
      <div style={{
        position: "absolute", left: px(m), top: px(1.5), width: px(5), height: px(3.2),
        fontSize: pt(150), color: numColor, fontFamily: skin.fontHero, lineHeight: 1,
      }}>{numText}</div>

      {/* 章节标题（divider.ts:24-28） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(4.55), width: px(W - 2 * m), height: px(1.0),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle + 4)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400,
        }}
      />

      {/* 一句话（divider.ts:29-34） */}
      {(c.subtitle || editable) && (
        <EditableText
          value={c.subtitle ?? ""} path="subtitle" editable={editable}
          onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
          style={{
            position: "absolute", left: px(m), top: px(5.6), width: px(W - 2 * m - 2), height: px(0.5),
            fontSize: pt(skin.scale.sectionHead), color: skin.sub, fontFamily: skin.fontBody,
          }}
        />
      )}

      {/* 底部 meta（divider.ts:37-40） */}
      <div style={{
        position: "absolute", left: px(m), top: px(6.9), width: px(W - 2 * m), height: px(0.3),
        fontSize: pt(skin.scale.meta), color: skin.sub, fontFamily: skin.fontMono, letterSpacing: "0.15em",
      }}>{footOf()}</div>

      {/* ink（043 §4.6）：朱印落款——章节页右下角（与封面闭环） */}
      <InkSeal skin={skin} x={W - m - 0.62} y={5.6} />
    </>
  );
}
